import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Mail,
  Lock,
  Workflow,
  Fingerprint,
  MessagesSquare,
  Award,
  KeyRound,
  ArrowLeft,
  Sparkles,
  UserRound,
  DoorOpen,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useAuthStore } from '../store/authStore';
import { useSurfaceTheme } from '../hooks/useSurfaceTheme';
import { previsualizarSala, fechaSalaHumana, type SalaEntrada } from '../lib/sala';
import { ApiError, apiFetch } from '../lib/api';

interface Feature {
  icon: LucideIcon;
  titulo: string;
  detalle: string;
}

// Texto real de dominio público GT — sin lorem, sin jerga de diseño.
const features: Feature[] = [
  {
    icon: Workflow,
    titulo: 'Procesos participativos',
    detalle: 'Del problema de la institución a una deliberación con método.',
  },
  {
    icon: Fingerprint,
    titulo: 'Trazabilidad total',
    detalle: 'Cada aporte queda en el acta, con autor y momento.',
  },
  {
    icon: MessagesSquare,
    titulo: 'De la voz al proyecto',
    detalle: 'Las ideas ciudadanas se vuelven una propuesta con evidencia.',
  },
  {
    icon: Award,
    titulo: 'Reconocimiento de coautoría',
    detalle: 'Quien aporta, figura: la contribución no se pierde.',
  },
];

/**
 * Qué sala abre el código que se está escribiendo, ANTES de dar el PIN.
 *
 * Es el antídoto a «entré a la sala equivocada»: un código viejo deja de
 * meterte en silencio a otro proceso — se lee el nombre de la sala y se frena.
 * Debounce de 400 ms para no consultar en cada tecla.
 */
function useSalaDelCodigo(codigo: string) {
  const [estado, setEstado] = useState<'vacio' | 'cargando' | 'ok' | 'error'>('vacio');
  const [sala, setSala] = useState<SalaEntrada | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);

  useEffect(() => {
    const limpio = codigo.trim();
    if (limpio.length < 4) {
      setEstado('vacio');
      setSala(null);
      setMensaje(null);
      return;
    }
    let vivo = true;
    setEstado('cargando');
    const t = window.setTimeout(() => {
      previsualizarSala(limpio)
        .then((s) => {
          if (!vivo) return;
          setSala(s);
          setMensaje(null);
          setEstado('ok');
        })
        .catch((err: unknown) => {
          if (!vivo) return;
          setSala(null);
          setMensaje(err instanceof ApiError ? err.detail : 'No pudimos leer ese código.');
          setEstado('error');
        });
    }, 400);
    return () => {
      vivo = false;
      window.clearTimeout(t);
    };
  }, [codigo]);

  return { estado, sala, mensaje };
}

/** Tarjeta «vas a entrar a…»: la sala con nombre, institución y fecha. */
function SalaDestino({
  estado,
  sala,
  mensaje,
}: ReturnType<typeof useSalaDelCodigo>) {
  if (estado === 'vacio') return null;

  if (estado === 'cargando') {
    return (
      <p className="mt-4 flex items-center gap-2 font-sans text-sm text-content-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Buscando esa sala…
      </p>
    );
  }

  if (estado === 'error' || !sala) {
    return (
      <p
        role="status"
        className="mt-4 flex items-start gap-2 rounded-md border border-error px-3 py-2 font-sans text-sm text-error"
      >
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        {mensaje ?? 'No encontramos esa sala.'}
      </p>
    );
  }

  const cuando = fechaSalaHumana(sala.sesion_fecha);
  return (
    <div
      role="status"
      className="mt-4 rounded-lg border border-primary bg-surface-sunken px-3 py-3"
    >
      <p className="flex items-center gap-2 font-sans text-xs font-semibold uppercase tracking-wide text-content-muted">
        <DoorOpen className="h-4 w-4 text-primary" aria-hidden="true" />
        Vas a entrar a
      </p>
      <p className="mt-1 font-display text-base font-semibold text-content">
        {sala.proceso_nombre}
      </p>
      <p className="mt-0.5 font-sans text-sm text-content-muted">
        {[sala.org_nombre, cuando].filter(Boolean).join(' · ')}
      </p>
    </div>
  );
}

/** Marco de las tres vías (mismo estilo, en el orden congelado). */
function VerticalDivider({ texto }: { texto: string }) {
  return (
    <div className="flex items-center gap-3" aria-hidden="true">
      <span className="h-px flex-1 bg-border" />
      <span className="font-sans text-xs font-medium uppercase tracking-wide text-content-muted">
        {texto}
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

export function LoginPage() {
  // El login corre en claro: el panel izquierdo pinta navy con su propio token.
  useSurfaceTheme('bolsillo', 'light');

  const navigate = useNavigate();
  const loginLocal = useAuthStore((s) => s.loginLocal);
  const loginSala = useAuthStore((s) => s.loginSala);
  const loginGoogle = useAuthStore((s) => s.loginGoogle);
  const error = useAuthStore((s) => s.error);
  const clearError = useAuthStore((s) => s.clearError);

  // Prellenado por URL (QR de la Pantalla): /login?sala=<código>&pin=<pin>.
  // El PIN en la URL es deliberado — es dato público de proyección (Lote 6).
  // Solo se usa como estado INICIAL: lo que el usuario escriba después manda.
  const [searchParams] = useSearchParams();
  const salaParam = searchParams.get('sala');
  const pinParam = searchParams.get('pin');
  // Multitenant en la puerta: /login?org=<slug> viste el login con la
  // identidad del tenant (nombre + logos de la cascada instancia→org,
  // ADR-0003) ANTES de que exista sesión. Endpoint público de solo lectura.
  const orgParam = searchParams.get('org');
  const tenant = useQuery({
    queryKey: ['tenant', orgParam],
    queryFn: () =>
      apiFetch<{ nombre: string; slug: string; logos: { url: string; etiqueta: string }[]; atribucion: string | null }>(
        `/auth/tenant/${orgParam}`,
      ),
    enabled: Boolean(orgParam),
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const vieneDeQr = Boolean(salaParam || pinParam);
  // QR sin PIN compartido (sprint multi-sesión): Google es la vía protagonista
  // — quien entra una vez queda registrado para todas las sesiones del proceso.
  const llegaSinPin = Boolean(salaParam) && !pinParam;

  const [correo, setCorreo] = useState('');
  const [password, setPassword] = useState('');
  const [nombre, setNombre] = useState('');
  const [codigo, setCodigo] = useState(salaParam ?? '');
  const [pin, setPin] = useState((pinParam ?? '').replace(/\D/g, '').slice(0, 4));
  const [enviando, setEnviando] = useState<'local' | 'sala' | null>(null);

  const nombreRef = useRef<HTMLInputElement>(null);
  const seccionSalaRef = useRef<HTMLDivElement>(null);

  // Qué sala abre el código escrito/pegado (o el del QR): se muestra antes
  // de pedir el PIN, así nadie entra a ciegas a la sala equivocada.
  const salaDestino = useSalaDelCodigo(codigo);

  // Al llegar con params, la vía de sala es la protagonista: se acerca la
  // sección (importa en móvil, donde queda bajo el pliegue) y el foco cae en
  // «Tu nombre», el único campo que falta llenar.
  useEffect(() => {
    if (!vieneDeQr) return;
    seccionSalaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Sin PIN compartido el protagonista es el botón de Google: no se roba el
    // foco al formulario alternativo. Con PIN, el único campo que falta es el nombre.
    if (!llegaSinPin) nombreRef.current?.focus({ preventScroll: true });
    // Solo al montar: los params no cambian dentro de la página de login.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmitLocal(e: FormEvent) {
    e.preventDefault();
    if (enviando) return;
    setEnviando('local');
    try {
      await loginLocal({ correo, password });
      navigate('/', { replace: true });
    } catch {
      /* el error humano ya está en el store */
    } finally {
      setEnviando(null);
    }
  }

  async function onSubmitSala(e: FormEvent) {
    e.preventDefault();
    if (enviando) return;
    setEnviando('sala');
    try {
      await loginSala({ codigo, pin, nombre });
      // Decisión del owner: al entrar por sala, el Bolsillo muestra primero
      // una confirmación breve de a dónde se entra (contexto, no fricción).
      navigate('/app?bienvenida=1', { replace: true });
    } catch {
      /* idem */
    } finally {
      setEnviando(null);
    }
  }

  return (
    <div className="grid min-h-screen bg-surface-base lg:grid-cols-2">
      {/* ---------- Panel izquierdo: navy, logo negativo, claim, features ---- */}
      <aside className="relative hidden flex-col justify-between bg-navy px-10 py-12 lg:flex">
        {/* Acento de marca puntual, no fondo genérico. */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-brand-gradient"
          aria-hidden="true"
        />
        <div>
          <img src="/brand/logonegativo.png" alt="ÁGORA" className="h-12 w-auto" />
          {tenant.data && (
            <div className="mt-6 flex flex-wrap items-center gap-4 rounded-lg bg-navy-700/60 px-4 py-3">
              {tenant.data.logos.map((l: { url: string; etiqueta: string }) => (
                <img key={l.url} src={l.url} alt={l.etiqueta} className="h-10 w-auto" />
              ))}
              <span className="font-sans text-sm font-semibold text-content-inverse">
                {tenant.data.nombre}
              </span>
            </div>
          )}
          <p className="mt-8 max-w-md font-display text-2xl font-semibold leading-snug text-content-inverse">
            La plaza digital de tu institución: cada voz queda en el acta y la
            deliberación termina en un proyecto.
          </p>
        </div>

        <ul className="mt-10 flex flex-col gap-5">
          {features.map(({ icon: Icon, titulo, detalle }) => (
            <li key={titulo} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-navy-700">
                <Icon className="h-5 w-5 text-cyan" aria-hidden="true" />
              </span>
              <div>
                <p className="font-display text-base font-semibold text-content-inverse">
                  {titulo}
                </p>
                <p className="font-sans text-sm text-ice-200">{detalle}</p>
              </div>
            </li>
          ))}
        </ul>

        <p className="mt-10 font-sans text-xs text-ice-200">
          Innovación participativa para instituciones públicas
        </p>
      </aside>

      {/* ---------- Panel derecho: claro, formulario ------------------------ */}
      <main className="flex flex-col justify-center px-5 py-10 sm:px-10">
        <div className="mx-auto w-full max-w-md">
          {/* Logo también visible en móvil (sin panel izquierdo). */}
          <img
            src="/brand/logopositivo.png"
            alt="ÁGORA"
            className="mb-8 h-9 w-auto lg:hidden"
          />
          {tenant.data && (
            <div className="-mt-4 mb-6 flex flex-wrap items-center gap-3 lg:hidden">
              {tenant.data.logos.map((l: { url: string; etiqueta: string }) => (
                <img key={l.url} src={l.url} alt={l.etiqueta} className="h-8 w-auto" />
              ))}
              <span className="font-sans text-sm font-semibold text-content">
                {tenant.data.nombre}
              </span>
            </div>
          )}

          <h1 className="font-display text-2xl font-bold text-content">Ingresá a ÁGORA</h1>
          <p className="mt-1 font-sans text-sm text-content-muted">
            Elegí cómo querés entrar a tu proceso participativo.
          </p>

          {error && (
            <div
              role="alert"
              className="mt-5 rounded-md border border-error bg-surface-raised px-4 py-3 text-sm text-error"
            >
              {error}
            </div>
          )}

          {/* Vía 1 — Correo + contraseña */}
          <form onSubmit={onSubmitLocal} className="mt-6 flex flex-col gap-4" noValidate>
            <Input
              label="Correo institucional"
              type="email"
              autoComplete="email"
              leadingIcon={<Mail className="h-4 w-4" aria-hidden="true" />}
              placeholder="nombre@institucion.gob.gt"
              value={correo}
              onChange={(e) => {
                setCorreo(e.target.value);
                if (error) clearError();
              }}
              required
            />
            <Input
              label="Contraseña"
              type="password"
              autoComplete="current-password"
              leadingIcon={<Lock className="h-4 w-4" aria-hidden="true" />}
              placeholder="Tu contraseña"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) clearError();
              }}
              required
            />
            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={enviando === 'local'}
              leadingIcon={<Mail className="h-4 w-4" aria-hidden="true" />}
            >
              Entrar con correo
            </Button>
          </form>

          {/* Vía 2 — Google */}
          <div className="mt-6">
            <VerticalDivider texto="o" />
            <Button
              type="button"
              variant="secondary"
              fullWidth
              className="mt-4"
              onClick={() => loginGoogle(salaParam ?? undefined)}
              leadingIcon={<Sparkles className="h-4 w-4 text-violet" aria-hidden="true" />}
            >
              {salaParam ? 'Entrar a la sala con Google' : 'Continuar con Google'}
            </Button>
          </div>

          {/* Vía 3 — Acceso de sala (código + PIN) */}
          <div ref={seccionSalaRef} className="mt-6">
            <VerticalDivider texto="Acceso de sala" />

            {/* A QUÉ SALA se entra, antes del PIN: con un código viejo se lee
                el proceso equivocado acá y se frena a tiempo. */}
            <SalaDestino {...salaDestino} />

            {/* QR sin PIN compartido: Google primero (contrato congelado —
                loginGoogle(sala) manda ?sala= al backend y la membresía queda
                para todo el proceso). El formulario baja a alternativa. */}
            {llegaSinPin && (
              <div className="mt-4 flex flex-col gap-2">
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={() => loginGoogle(salaParam!)}
                  leadingIcon={<Sparkles className="h-5 w-5" aria-hidden="true" />}
                >
                  Entrar con Google
                </Button>
                <p className="font-sans text-sm text-content-muted">
                  Quedás dentro del proceso y registrado para todas sus sesiones.
                </p>
              </div>
            )}

            <form onSubmit={onSubmitSala} className="mt-4 flex flex-col gap-4" noValidate>
              {llegaSinPin ? (
                <p className="font-sans text-sm text-content-muted">
                  ¿Tenés un PIN personal? Entrá con tu nombre.
                </p>
              ) : (
                <p className="font-sans text-sm text-content-muted">
                  Si estás en un taller presencial, escribí tu nombre y usá el
                  código y el PIN que se proyectan en la Pantalla del salón.
                </p>
              )}
              <Input
                ref={nombreRef}
                label="Tu nombre"
                type="text"
                autoComplete="name"
                leadingIcon={<UserRound className="h-4 w-4" aria-hidden="true" />}
                placeholder="María del Carmen, del COCODE"
                value={nombre}
                onChange={(e) => {
                  setNombre(e.target.value);
                  if (error) clearError();
                }}
                required
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Código de sesión"
                  inputMode="text"
                  autoCapitalize="characters"
                  placeholder="SALA-01"
                  value={codigo}
                  onChange={(e) => {
                    setCodigo(e.target.value);
                    if (error) clearError();
                  }}
                  required
                />
                <Input
                  label="PIN de sala"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  placeholder="4 dígitos"
                  leadingIcon={<KeyRound className="h-4 w-4" aria-hidden="true" />}
                  value={pin}
                  onChange={(e) => {
                    setPin(e.target.value.replace(/\D/g, '').slice(0, 4));
                    if (error) clearError();
                  }}
                  required
                />
              </div>
              <Button
                type="submit"
                variant="secondary"
                fullWidth
                loading={enviando === 'sala'}
                leadingIcon={<KeyRound className="h-4 w-4" aria-hidden="true" />}
              >
                Entrar a la sala
              </Button>
            </form>

            {/* QR con PIN: el orden actual se mantiene (nombre primero) y
                Google queda como alternativa, también atado a la sala. */}
            {vieneDeQr && !llegaSinPin && (
              <Button
                type="button"
                variant="secondary"
                fullWidth
                className="mt-4"
                onClick={() => loginGoogle(salaParam ?? codigo)}
                leadingIcon={<Sparkles className="h-4 w-4 text-violet" aria-hidden="true" />}
              >
                Entrar con Google y quedar en el proceso
              </Button>
            )}
          </div>

          {/* Pie: volver al inicio + badge de marca */}
          <div className="mt-8 flex flex-col items-start gap-4 border-t border-border pt-6">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 font-sans text-sm font-medium text-primary hover:underline"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Volver al inicio
            </Link>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-sunken px-3 py-1 font-sans text-xs font-medium text-content-muted">
              <Sparkles className="h-3.5 w-3.5 text-violet" aria-hidden="true" />
              Powered by Grupo InteligencIA
            </span>
            <p className="font-sans text-xs text-content-muted">
              Al continuar aceptás las{' '}
              <Link to="/terminos" className="font-medium text-primary hover:underline">
                Condiciones del Servicio
              </Link>{' '}
              y la{' '}
              <Link to="/privacidad" className="font-medium text-primary hover:underline">
                Política de Privacidad
              </Link>
              .
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
